import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Users, Search, Plus, MoreHorizontal, Pencil, KeyRound, UserX, UserCheck,
  Loader2, Copy, ShieldAlert, Mail, Clock, Sparkles, History, ChevronLeft, ChevronRight,
  Download, FileText, ShieldCheck,
} from 'lucide-react';
import { usersApi, userAuditApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { cn, roleConfig, roleOptions, formatDateTime } from '../../lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const DEFAULT_NEW_USER_ROLE = 'colaborador';

const EMPTY_FORM = {
  email: '',
  name: '',
  role: DEFAULT_NEW_USER_ROLE,
  department: '',
  password: '',
};

export default function UsersManagement() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    if (currentUser && !isAdmin) navigate('/dashboard');
  }, [currentUser, isAdmin, navigate]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Deactivate confirmation
  const [deactivating, setDeactivating] = useState(null);

  // Password reset result
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  async function load() {
    setLoading(true);
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (statusFilter === 'active' && u.active === false) return false;
      if (statusFilter === 'inactive' && u.active !== false) return false;
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(u.email?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [users, statusFilter, roleFilter, search]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.active !== false).length,
    inactive: users.filter(u => u.active === false).length,
    mustChange: users.filter(u => u.must_change_password).length,
  }), [users]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name || '',
      role: u.role || 'colaborador',
      department: u.department || '',
      password: '',
    });
    setDialogOpen(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await usersApi.update(editing.user_id, {
          name: form.name,
          role: form.role,
          department: form.department || null,
        });
        toast.success('Utilizador atualizado');
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error('A palavra-passe inicial deve ter pelo menos 8 caracteres');
          setSaving(false);
          return;
        }
        await usersApi.create({
          email: form.email,
          name: form.name,
          password: form.password,
          role: form.role,
          department: form.department || null,
        });
        toast.success('Utilizador criado. Alteração de palavra-passe será exigida no primeiro login.');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword(u) {
    try {
      const res = await usersApi.resetPassword(u.user_id);
      setResetResult({ email: u.email, ...res.data });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao reiniciar palavra-passe');
    }
  }

  async function handleToggleActive(u) {
    if (u.active === false) {
      // Reactivate
      try {
        await usersApi.update(u.user_id, { active: true });
        toast.success(`Conta de ${u.email} reativada`);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erro ao reativar');
      }
    } else {
      setDeactivating(u);
    }
  }

  async function confirmDeactivate() {
    if (!deactivating) return;
    try {
      await usersApi.deactivate(deactivating.user_id);
      toast.success(`Conta de ${deactivating.email} desativada`);
      setDeactivating(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao desativar');
    }
  }

  if (!isAdmin) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-management-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-indigo-600" />
              Utilizadores
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Gestão multi-tenant de contas · <strong>{stats.active}</strong> ativas · <strong>{stats.inactive}</strong> desativadas · <strong>{stats.mustChange}</strong> pendentes de rotação
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm"
            data-testid="users-create-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Criar utilizador
          </Button>
        </div>

        <Tabs defaultValue="accounts" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="accounts" data-testid="tab-accounts">
              <Users className="h-4 w-4 mr-1.5" />Contas
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="h-4 w-4 mr-1.5" />Histórico
            </TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="space-y-6 m-0">

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <KpiCard label="Total" value={stats.total} tone="slate" />
          <KpiCard label="Ativas" value={stats.active} tone="emerald" />
          <KpiCard label="Desativadas" value={stats.inactive} tone="red" />
          <KpiCard label="Pendem rotação" value={stats.mustChange} tone="amber" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3 flex-wrap items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome ou email..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="users-search-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[200px]" data-testid="users-role-filter">
                  <SelectValue placeholder="Papel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os papéis</SelectItem>
                  {roleOptions.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="users-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  <SelectItem value="active">Ativas</SelectItem>
                  <SelectItem value="inactive">Desativadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contas ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />A carregar utilizadores…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">Nenhum utilizador encontrado.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-3">Utilizador</th>
                      <th className="px-4 py-3">Papel</th>
                      <th className="px-4 py-3">Departamento</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Último login</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(u => {
                      const cfg = roleConfig[u.role] || { label: u.role, color: 'bg-slate-50 text-slate-700' };
                      const isSelf = u.user_id === currentUser?.user_id;
                      return (
                        <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                'h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0',
                                u.active === false ? 'bg-slate-100 text-slate-400' : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                              )}>
                                {(u.name || u.email || '?').slice(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-slate-900 truncate">{u.name || '(sem nome)'}</p>
                                  {isSelf && <Badge variant="outline" className="text-[10px]">Você</Badge>}
                                </div>
                                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {u.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={cn('text-[11px]', cfg.color)}>{cfg.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.department || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              {u.active === false ? (
                                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Desativada</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ativa</Badge>
                              )}
                              {u.must_change_password && (
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`user-mcp-${u.email}`}>
                                  <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />Rotação pendente
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {u.last_login_at ? (
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDateTime(u.last_login_at)}</span>
                            ) : (
                              <span className="text-slate-400 italic">nunca</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.email}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`user-edit-${u.email}`}>
                                  <Pencil className="h-4 w-4 mr-2" />Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResetPassword(u)} data-testid={`user-reset-${u.email}`}>
                                  <KeyRound className="h-4 w-4 mr-2" />Reiniciar palavra-passe
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {isSelf ? (
                                  <DropdownMenuItem disabled className="text-slate-400">
                                    <UserX className="h-4 w-4 mr-2" />Não pode desativar-se
                                  </DropdownMenuItem>
                                ) : u.active === false ? (
                                  <DropdownMenuItem onClick={() => handleToggleActive(u)} className="text-emerald-700" data-testid={`user-reactivate-${u.email}`}>
                                    <UserCheck className="h-4 w-4 mr-2" />Reativar conta
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleToggleActive(u)} className="text-red-600" data-testid={`user-deactivate-${u.email}`}>
                                    <UserX className="h-4 w-4 mr-2" />Desativar conta
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="history" className="m-0">
            <AuditHistoryTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar utilizador' : 'Criar utilizador'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Atualize o perfil deste utilizador. O email não é editável.' : 'A alteração de palavra-passe será exigida no primeiro login.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="uf-email">Email</Label>
              <Input
                id="uf-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editing}
                required
                placeholder="nome@empresa.pt"
                data-testid="uf-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="uf-name">Nome</Label>
              <Input
                id="uf-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Ex: Maria Silva"
                data-testid="uf-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="uf-role">Papel</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger id="uf-role" data-testid="uf-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="uf-dept">Departamento</Label>
                <Input
                  id="uf-dept"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="Opcional"
                  data-testid="uf-dept"
                />
              </div>
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label htmlFor="uf-pw">Palavra-passe temporária</Label>
                <Input
                  id="uf-pw"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                  required
                  data-testid="uf-pw"
                />
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />O utilizador terá de alterá-la no primeiro login.
                </p>
              </div>
            )}
            <DialogFooter className="pt-3">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                data-testid="uf-submit"
              >
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {editing ? 'Guardar' : 'Criar conta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <AlertDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
        <AlertDialogContent data-testid="deactivate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar conta?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivating && (
                <>
                  A conta de <strong>{deactivating.email}</strong> deixará de poder aceder à plataforma.
                  Esta ação é reversível (podes reativar depois). Os registos históricos são preservados.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeactivate} className="bg-red-600 hover:bg-red-700" data-testid="deactivate-confirm-btn">
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password reset result */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-md" data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-indigo-600" />
              Palavra-passe temporária gerada
            </DialogTitle>
            <DialogDescription>
              Partilha esta palavra-passe com <strong>{resetResult?.email}</strong> por um canal seguro. Será obrigada a alterá-la no próximo login.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-2">
            <div className="flex items-center justify-between gap-3">
              <code className="text-lg font-mono text-slate-900 flex-1 select-all" data-testid="reset-temp-password">
                {resetResult?.temporary_password}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(resetResult.temporary_password);
                  toast.success('Copiada para a área de transferência');
                }}
                data-testid="reset-copy-btn"
              >
                <Copy className="h-3.5 w-3.5 mr-1" />Copiar
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} data-testid="reset-close">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function KpiCard({ label, value, tone }) {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={cn('border rounded-lg p-4', toneMap[tone])} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[11px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}


// -------- Audit History tab --------

const ACTION_ICON = {
  'user.created': { icon: Plus, color: 'bg-emerald-100 text-emerald-700' },
  'user.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700' },
  'user.deactivated': { icon: UserX, color: 'bg-red-100 text-red-700' },
  'user.reactivated': { icon: UserCheck, color: 'bg-emerald-100 text-emerald-700' },
  'user.password_changed': { icon: KeyRound, color: 'bg-indigo-100 text-indigo-700' },
  'user.password_reset_by_admin': { icon: ShieldAlert, color: 'bg-amber-100 text-amber-700' },
};

function AuditHistoryTab() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0, available_actions: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false); // false | 'csv' | 'pdf'
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [debouncedActor, setDebouncedActor] = useState('');
  const [debouncedTarget, setDebouncedTarget] = useState('');
  const pageSize = 25;

  // Debounce free-text filters
  useEffect(() => {
    const t = setTimeout(() => setDebouncedActor(actorFilter), 300);
    return () => clearTimeout(t);
  }, [actorFilter]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTarget(targetFilter), 300);
    return () => clearTimeout(t);
  }, [targetFilter]);

  useEffect(() => { load(); }, [page, actionFilter, debouncedActor, debouncedTarget]);
  useEffect(() => { loadStats(); }, []);

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (debouncedActor) params.actor_email = debouncedActor;
      if (debouncedTarget) params.target_email = debouncedTarget;
      const res = await userAuditApi.list(params);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }
  async function loadStats() {
    try { const r = await userAuditApi.stats(); setStats(r.data); } catch (_) { /* silent */ }
  }

  async function handleExport(format = 'csv') {
    setExporting(format);
    try {
      const params = {};
      if (actionFilter !== 'all') params.action = actionFilter;
      if (debouncedActor) params.actor_email = debouncedActor;
      if (debouncedTarget) params.target_email = debouncedTarget;
      const res = format === 'pdf'
        ? await userAuditApi.exportPdf(params)
        : await userAuditApi.exportCsv(params);
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `nis2ai_user_audit_${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const totalRecords = res.headers?.['x-total-records'] || '?';
      if (format === 'pdf') {
        const shortHash = (res.headers?.['x-content-hash-sha256'] || '').slice(0, 12);
        toast.success(`Exportados ${totalRecords} eventos em PDF assinado · SHA-256: ${shortHash}…`);
      } else {
        toast.success(`Exportados ${totalRecords} eventos para CSV`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao exportar');
    } finally {
      setExporting(false);
    }
  }

  const resetFilters = () => {
    setActionFilter('all'); setActorFilter(''); setTargetFilter(''); setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="audit-history-tab">
      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard label="Total de eventos" value={stats.total} tone="slate" />
          <KpiCard label="Últimas 24h" value={stats.today} tone="emerald" />
          <KpiCard label="Últimos 7 dias" value={stats.last_7_days} tone="amber" />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3 flex-wrap items-center">
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[240px]" data-testid="audit-action-filter">
                <SelectValue placeholder="Ação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as ações</SelectItem>
                {data.available_actions.map(a => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Actor (email)…"
                className="pl-10"
                value={actorFilter}
                onChange={(e) => { setActorFilter(e.target.value); setPage(1); }}
                data-testid="audit-actor-filter"
              />
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Alvo (email)…"
                className="pl-10"
                value={targetFilter}
                onChange={(e) => { setTargetFilter(e.target.value); setPage(1); }}
                data-testid="audit-target-filter"
              />
            </div>
            {(actionFilter !== 'all' || actorFilter || targetFilter) && (
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="audit-reset-filters">Limpar</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Events feed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
            <span>Eventos ({data.total})</span>
            <div className="flex items-center gap-3">
              {data.total_pages > 1 && (
                <span className="text-xs font-normal text-slate-500">Página {page} de {data.total_pages}</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={!!exporting || data.total === 0}
                data-testid="audit-export-csv-btn"
              >
                {exporting === 'csv' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                Exportar CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
                disabled={!!exporting || data.total === 0}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="audit-export-pdf-btn"
                title="PDF assinado com hash SHA-256 (evidência legal)"
              >
                {exporting === 'pdf' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                PDF Assinado
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />A carregar histórico…
            </div>
          ) : data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="audit-empty">
              Nenhum evento encontrado para os filtros aplicados.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.items.map((ev) => {
                const cfg = ACTION_ICON[ev.action] || { icon: History, color: 'bg-slate-100 text-slate-700' };
                const Icon = cfg.icon;
                return (
                  <li key={ev.audit_id} className="px-4 py-3 hover:bg-slate-50" data-testid={`audit-event-${ev.audit_id}`}>
                    <div className="flex items-start gap-3">
                      <div className={cn('h-8 w-8 rounded-full flex items-center justify-center shrink-0', cfg.color)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-900">{ev.action_label}</p>
                          <Badge variant="outline" className="text-[10px] font-mono">{ev.audit_id}</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">
                          <span className="text-slate-500">Actor:</span> <strong>{ev.actor_email || '—'}</strong>
                          {ev.target_email && (
                            <>
                              {' · '}
                              <span className="text-slate-500">Alvo:</span> <strong>{ev.target_email}</strong>
                            </>
                          )}
                        </p>
                        {ev.details && Object.keys(ev.details).length > 0 && (
                          <AuditDetails details={ev.details} />
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 shrink-0 whitespace-nowrap">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(ev.logged_at)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="audit-pagination">
          <Button
            variant="outline" size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          ><ChevronLeft className="h-4 w-4 mr-1" />Anterior</Button>
          <span className="text-xs text-slate-500 px-2">
            {page} / {data.total_pages}
          </span>
          <Button
            variant="outline" size="sm"
            onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
            disabled={page >= data.total_pages}
          >Próxima<ChevronRight className="h-4 w-4 ml-1" /></Button>
        </div>
      )}
    </div>
  );
}

function AuditDetails({ details }) {
  // Render "changes" specifically, otherwise fallback to key/value list
  if (details.changes && typeof details.changes === 'object') {
    return (
      <div className="mt-2 space-y-0.5">
        {Object.entries(details.changes).map(([k, v]) => (
          <p key={k} className="text-[11px] text-slate-600">
            <span className="text-slate-400 uppercase tracking-widest text-[9px]">{k}:</span>{' '}
            <s className="text-slate-400">{v.from ?? '—'}</s>{' → '}
            <strong className="text-slate-800">{v.to ?? '—'}</strong>
          </p>
        ))}
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {Object.entries(details).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
          <span className="text-slate-500">{k}:</span> {String(v)}
        </span>
      ))}
    </div>
  );
}
